/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, forwardRef, input, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { AbstractControlValueAccessorGroup } from '@irian-ro/shared-components-module';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AddressDTO, PointDtoModel } from '@well/address-management-domain-model';
import { AddressInputsComponent } from '../address-inputs/address-inputs.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { AddressAsStringViewComponent } from './address-as-string-view/address-as-string-view.component';
import { AddressSearchBoxComponent } from '../address-searchbox/address-search-box.component';
import { TranslateDropdownStringListPipe } from '@irian-ro/shared-ui';
import {
  OfficeAddressInfoMessageComponent
} from '../office-address-info-message/office-address-info-message.component';
import { LocalAddressSearchService } from '../address-searchbox/local-address-search/local-address-search.service';

@Component({
  selector: 'well-address-picker',
  templateUrl: './address-picker.component.html',
  styleUrls: ['./address-picker.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    AddressInputsComponent,
    ReactiveFormsModule,
    ButtonModule,
    TranslateModule,
    AddressAsStringViewComponent,
    AddressSearchBoxComponent,
    TranslateDropdownStringListPipe,
    OfficeAddressInfoMessageComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressPickerComponent),
      multi: true,
    },
    LocalAddressSearchService
  ],
})
export class AddressPickerComponent extends AbstractControlValueAccessorGroup implements OnDestroy {
  onDestroy: Subject<any> = new Subject<any>();

  allowSelection = input(true);

  @Input() validateForDistance = true;

  @Input() displayTypeField = false;

  @Input() displayVisibilityField = false;

  @Input() searchLocal = true;

  @Input() searchOnGoolge = true;

  @Input() meta: any = null;

  @Input() displayAddressTemplateSave = false;

  @Input() disableFieldsUntilInput = true;

  @Input() hideFieldsUntilInput = false;

  @Input() mapFloatRight = false;

  @Input() mapHeight: number | undefined = undefined;

  @Input() displayAsString = false;

  @Input() editString = true;

  @Input() handoverAddress = false;

  @Input() companyRequired = false;

  @Input() showAddressLabel = true;

  @Input() showAddressSearch = true;

  @Input() displayMainAddress = true;

  @Input() showOfficeAddressInfoMessage = false;

  @Input() allowCustomerSelection = false;
  @Input() eTransportContext = false;
  @Input() displayAdditionalFields = false;
  @Input() showClear = false;

  @Output() addressSelect = new EventEmitter<void>();

  showDetails = true;
  showMap = true; // Flag for handling the map visibility after address template save

  initialValue: any | undefined = undefined;

  constructor() {
    super();
  }

  writeValue(obj: any): void {
    if (obj) {
      this.form.setValue({ address: obj, select: obj }, { emitEvent: false });
      if (obj.address?.pointDTO) {
        this.disableFieldsUntilInput = false;
      }
      this.initialValue = obj;
      if (this.displayAsString && obj.postalCode) {
        this.showDetails = false;
      }
    } else if (obj == null && this.eTransportContext) {
      // TODO: check if eTransportContext flag is needed
      this.onAddressSelect(null);
      this.disableFieldsUntilInput = false;
      this.hideFieldsUntilInput = true;
    } else if (!obj) {
      this.form.setValue({ address: null, select: null }, { emitEvent: true });
      this.hideFieldsUntilInput = true;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(true);
  }

  protected createForm() {
    this.form = new FormGroup({
      select: new FormControl(null),
      address: new FormControl(null),
    });
  }

  onAddressSelect(newAddress: AddressDTO) {
    if (!newAddress) {
      this.form.setValue({ address: null, select: null }, { emitEvent: true });
      return;
    }
    if (newAddress) {
      const currentAddress = (<FormGroup>this.form).get('address')?.value || {};

      const updatedAddress = {
        ...currentAddress,
        ...newAddress,
        uuid: currentAddress?.uuid,
        owner: currentAddress?.owner,
        addressTypeValue: currentAddress?.addressTypeValue,
        visibilityType: currentAddress?.visibilityType,
        businessProfileUuid: currentAddress?.businessProfileUuid,
        basedOnAddressTemplateUuid: this.allowCustomerSelection ? null : (newAddress?.uuid ?? null),
      };

      this.form.setValue({ address: updatedAddress, select: updatedAddress });
      if (newAddress?.pointDTO) {
        this.disableFieldsUntilInput = false;
      }
      this.onChange(updatedAddress);
      this.addressSelect.emit();
    } else {
      //TODO: Should be emitEvent: false, and handled in the parent component
      this.form.setValue({ address: null, select: null }, { emitEvent: true });
    }
  }

  protected onChangeCallback(res: any) {
    this.showMap = !!res.select;
    return res.select ? { ...res.address } : null;
  }

  onShowDetailsClick(event: boolean) {
    this.showDetails = event;
  }

  apply() {
    if (this.form.valid) {
      this.showDetails = false;
    }
  }

  onLocationChanged(event: { pointDTO: PointDtoModel }) {
    (<FormGroup>this.form).get('address')?.patchValue({ ...(<FormGroup>this.form).get('address')?.value, ...event });
  }

  validate() {
    return this.form.valid ? null : { invalid: true };
  }
}
